import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchOrder } from "@/lib/api";
import { useStore } from "@/lib/store";
import { CheckCircle2, Truck, Package, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function OrderConfirmedPage() {
  const { orderId } = useParams();
  const { format } = useStore();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrder(orderId)
      .then(setOrder)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[order-confirmed] fetchOrder failed", err?.response?.status);
        setError("Order not found");
      });
  }, [orderId]);

  if (error) return <main className="max-w-2xl mx-auto py-24 px-6 text-center">{error}</main>;
  if (!order) return <main className="max-w-2xl mx-auto py-24 px-6">
    <div className="h-40 sd-skeleton rounded-2xl" />
  </main>;

  return (
    <main data-testid="order-confirmed-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="text-center">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A059]"><Sparkles className="h-3.5 w-3.5" /> Thank you</div>
        <div className="mt-4 mx-auto w-14 h-14 rounded-full bg-[#C5A059]/15 border border-[#C5A059] flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-[#C5A059]" />
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-6">Your order is confirmed.</h1>
        <p className="mt-3 text-[#1A1918]/70">Order <span data-testid="order-id" className="font-medium">{order.id}</span> is now hand-poured and preparing for dispatch.</p>
      </motion.div>

      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl border border-black/10 bg-[#F1ECE4]">
          <Package className="h-5 w-5 text-[#C5A059]" />
          <div className="mt-3 text-xs uppercase tracking-[0.14em] text-[#1A1918]/60">Items</div>
          <div className="font-serif text-2xl mt-1">{order.lines.length}</div>
        </div>
        <div className="p-5 rounded-2xl border border-black/10 bg-[#F1ECE4]">
          <Truck className="h-5 w-5 text-[#C5A059]" />
          <div className="mt-3 text-xs uppercase tracking-[0.14em] text-[#1A1918]/60">Tracking</div>
          <div className="font-serif text-lg mt-1" data-testid="order-tracking">{order.tracking_number || "Preparing"}</div>
        </div>
        <div className="p-5 rounded-2xl border border-black/10 bg-[#F1ECE4]">
          <Sparkles className="h-5 w-5 text-[#C5A059]" />
          <div className="mt-3 text-xs uppercase tracking-[0.14em] text-[#1A1918]/60">Total</div>
          <div className="font-serif text-2xl mt-1" data-testid="order-total">{format(order.total)}</div>
        </div>
      </div>

      <div className="mt-10 rounded-2xl border border-black/10 bg-[#FAF8F5] p-6">
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60 mb-4">Line Items</div>
        {(order.line_items || []).map((item, i) => (
          <div key={`${item.product?.id || "line"}-${item.size_label}-${i}`} className="flex items-center gap-4 py-3 border-b border-black/8 last:border-none">
            <div className="w-14 h-16 rounded-md bg-[#F1ECE4]" style={{ backgroundImage: `url(${item.product.image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#1A1918]/60">{item.product.number}</div>
              <Link to={`/product/${item.product.slug}`} className="font-serif text-base hover:text-[#C5A059]">{item.product.name}</Link>
              <div className="text-xs text-[#1A1918]/60">{item.size_label} × {item.quantity}</div>
            </div>
            <div className="text-sm">{format(item.unit_price * item.quantity)}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link to="/shop" data-testid="order-continue-shopping" className="px-6 py-3 rounded-xl bg-[#C5A059] text-[#1A1918] text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059]/90 transition-colors">Continue Shopping</Link>
        <Link to="/account" className="px-6 py-3 rounded-xl border border-black/10 text-xs uppercase tracking-[0.14em] hover:border-[#C5A059]/40 transition-colors">View Account</Link>
      </div>
    </main>
  );
}
